import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Users, Phone, Mail, Building2, AlertTriangle } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, EmptyState, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Select } from '../../components/ui/Form.jsx';
import { computeDisplayStatus } from '../../lib/computeStatus.js';

export function ChauffeursListPage() {
    const api = useApi();
    const navigate = useNavigate();
    const { isAdmin } = useCurrentUser();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [categoryFilter, setCategoryFilter] = useState('');
        const [authorityFilter, setAuthorityFilter] = useState('');
    const [areaFilter, setAreaFilter] = useState('');
    const areasQuery = useQuery({
        queryKey: ['service-areas'],
        queryFn: () => api.get('/offers/meta/service-areas'),
    });
    const areaOptions = areasQuery.data?.areas || [];

    // Translate the UI chip value to what the backend understands.
    // "chauffeurs" chip = everyone who isn't a fleet partner (direct + fleet partner chauffeurs).
    // Backend doesn't have a single category for that, so we send status only and filter client-side.
    const backendCategory = categoryFilter === 'chauffeurs' ? undefined : categoryFilter || undefined;

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['drivers', { status: statusFilter, search, category: categoryFilter, licensing_authority: authorityFilter, service_area: areaFilter }],
    queryFn: () => api.get('/drivers', {
        query: {
            status: statusFilter || undefined,
            search: search || undefined,
            category: backendCategory,
                        licensing_authority: authorityFilter || undefined,
            service_area: areaFilter || undefined,
        }
    }),
});

    const allDrivers = data?.drivers || [];
    // When the "Chauffeurs" chip is active, filter out fleet partners client-side
    const chauffeurs = categoryFilter === 'chauffeurs'
        ? allDrivers.filter(d => !d.is_fleet_partner)
        : allDrivers;

    const actions = isAdmin ? (
        <Button onClick={() => navigate('/chauffeurs/new')}>
            <Plus className="h-4 w-4" />
            New chauffeur
        </Button>
    ) : null;

    const categoryChips = [
        { value: '', label: 'All' },
        { value: 'chauffeurs', label: 'Chauffeurs' },
               { value: 'fleet_partner', label: 'Operators' },
    ];

    return (
        <div>
            <PageHeader
                title="Chauffeurs"
                subtitle="Manage chauffeurs, documents, and vehicle pairings."
                actions={actions}
            />

            {/* Category filter chips */}
            <div className="mb-4 flex flex-wrap gap-2">
                {categoryChips.map(chip => (
                    <button
                        key={chip.value}
                        type="button"
                        onClick={() => setCategoryFilter(chip.value)}
                        className={
                            'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
                            (categoryFilter === chip.value
                                ? 'bg-ink text-paper border-ink'
                                : 'bg-paper text-ink-muted border-paper-border hover:border-ink hover:text-ink')
                        }
                    >
                        {chip.label}
                    </button>
                ))}
            </div>

            <div className="card mb-5">
                <div className="card-body">
                                       <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle pointer-events-none" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                                                placeholder="Search by name, email, call sign, or operator company…"
                                className="input pl-9"
                            />
                        </div>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="sm:w-44"
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                        </Select>

                        <Select
    value={authorityFilter}
    onChange={(e) => setAuthorityFilter(e.target.value)}
    className="sm:w-48"
    aria-label="Filter by authority"
>
    <option value="">All authorities</option>
    <option value="TFL">TFL</option>
    <option value="Castle Point">Castle Point</option>
        <option value="Other">Other</option>
</Select>

<Select
    value={areaFilter}
    onChange={(e) => setAreaFilter(e.target.value)}
    className="sm:w-44"
    aria-label="Filter by area"
>
    <option value="">All areas</option>
    {areaOptions.map((a) => (
        <option key={a} value={a}>{a}</option>
    ))}
</Select>
                    </div>
                </div>
            </div>

            {isLoading && <LoadingPage />}
            {isError && <ErrorPage message={error?.message} onRetry={refetch} />}

            {!isLoading && !isError && chauffeurs.length === 0 && (
                <EmptyState
                    icon={Users}
                    title="No chauffeurs found"
                    description={search || statusFilter !== 'active' || categoryFilter
                        ? 'Try adjusting your search or filters.'
                        : 'Add your first chauffeur to get started.'}
                    action={isAdmin && (
                        <Button onClick={() => navigate('/chauffeurs/new')}>
                            <Plus className="h-4 w-4" />
                            Add chauffeur
                        </Button>
                    )}
                />
            )}

            {!isLoading && !isError && chauffeurs.length > 0 && (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block card overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-paper-hover border-b border-paper-border text-xs font-medium text-ink-subtle uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-6 py-3 w-24">Call Sign</th>
                                    <th className="text-left px-6 py-3">Name</th>
                                    <th className="text-left px-6 py-3">Contact</th>
                                    <th className="text-left px-6 py-3">Authority</th>
                                    <th className="text-left px-6 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chauffeurs.map(c => (
                                    <tr
                                        key={c.id}
                                        onClick={() => navigate(`/chauffeurs/${c.id}`)}
                                        className="border-b border-paper-border-soft last:border-0 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <CallSignBadge callSign={c.call_sign} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-ink">{c.first_name} {c.last_name}</span>
                                                <CategoryBadge driver={c} />
                                                <AddressMissingBadge driver={c} />
<TimekeepingBadge driver={c} />
                                            </div>
                                            <div className="text-xs text-ink-subtle mt-0.5">Since {formatDate(c.start_date)}</div>
                                        </td>
                                        <td className="px-6 py-4 text-ink-muted">
                                                                                       {c.email && <div className="flex items-center gap-1.5 text-xs"><Mail className="h-3 w-3" />{c.email}</div>}
                                            {c.phone && <div className="flex items-center gap-1.5 text-xs mt-0.5"><Phone className="h-3 w-3" />{c.phone}</div>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-ink">{c.licensing_authority}</span>
                                        </td>
                                       <td className="px-6 py-4">
    <StatusWithReasons entity={c} />
</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {chauffeurs.map(c => (
                            <div
                                key={c.id}
                                onClick={() => navigate(`/chauffeurs/${c.id}`)}
                                className="card card-body cursor-pointer hover:shadow-card-hover transition-shadow"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <CallSignBadge callSign={c.call_sign} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-ink">{c.first_name} {c.last_name}</span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                                <CategoryBadge driver={c} />
                                                <AddressMissingBadge driver={c} />
<TimekeepingBadge driver={c} />
                                            </div>
                                            <div className="text-xs text-ink-subtle mt-1.5 truncate">{c.email}</div>
                                            <div className="text-xs text-ink-subtle mt-0.5">{c.phone}</div>
                                        </div>
                                    </div>
                                    <StatusWithReasons entity={c} />
                                </div>
                                <div className="mt-3 pt-3 border-t border-paper-border-soft flex items-center justify-between text-xs">
                                    <span className="text-ink-muted">{c.licensing_authority}</span>
                                    {c.include_in_tfl_report === false && c.category === 'direct' && (
                                        <StatusPill status="neutral">Excluded</StatusPill>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-4 text-xs text-ink-subtle">
                        {chauffeurs.length} {chauffeurs.length === 1 ? 'chauffeur' : 'chauffeurs'}
                    </p>
                </>
            )}
        </div>
    );
}

/**
 * Inline badge for the call sign. Shows "—" in muted grey for chauffeurs
 * who don't have one yet (pre-migration records).
 */
function CallSignBadge({ callSign }) {
    if (!callSign) {
        return <span className="text-ink-faint text-xs">—</span>;
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-ink text-paper font-mono text-xs font-medium tracking-wide">
            {callSign}
        </span>
    );
}

/**
 * Category badge — only renders for fleet partners and fleet chauffeurs.
 * Direct chauffeurs get no badge (to keep the list clean).
 */
function CategoryBadge({ driver }) {
    if (driver.is_fleet_partner) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                title="Operator — runs their own operator-licensed company"
            >
                <Building2 className="h-3 w-3" />
                {driver.fleet_partner_company_name || 'Operator'}
            </span>
        );
    }
    if (driver.fleet_partner_id) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                title="Works under an operator"
            >
                Under {driver.parent_partner_company_name || 'an operator'}
            </span>
        );
    }
    return null;
}

/**
 * Compliance warning badge — round 7b.6.
 * Shows when a chauffeur is missing the compliance-required home address, or
 * (for fleet partners) the operator address. Sourced from two booleans the
 * backend derives in the list endpoint: home_address_missing and
 * operator_address_missing.
 */
function AddressMissingBadge({ driver }) {
    if (!driver.home_address_missing && !driver.operator_address_missing) return null;

    const title = driver.home_address_missing && driver.operator_address_missing
        ? 'Home and operator addresses required for compliance — update before next report'
        : driver.home_address_missing
            ? 'Home address required for compliance — update before next report'
            : 'Operator address required for compliance — update before next report';

    const label = driver.home_address_missing && driver.operator_address_missing
        ? 'Addresses required'
        : driver.home_address_missing
            ? 'Address required'
            : 'Operator address required';

    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
            title={title}
        >
            <AlertTriangle className="h-3 w-3" />
            {label}
        </span>
    );
}

function TimekeepingBadge({ driver }) {
    if (!driver.early_arrival_minutes) return null;
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide bg-red-100 text-red-700 border border-red-300"
            title={`Must be ready at pickup ${driver.early_arrival_minutes} min early`}
        >
            Timekeeping
        </span>
    );
}

function statusVariant(status) {
    switch (status) {
        case 'active': return 'success';
        case 'inactive': return 'neutral';
        case 'suspended': return 'danger';
        default: return 'neutral';
    }
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusWithReasons({ entity }) {
    const { tone, label, reasons } = computeDisplayStatus(entity);

    if (reasons.length === 0) {
        return <StatusPill status={tone}>{label}</StatusPill>;
    }

    return (
        <div className="flex flex-col items-start gap-1">
            <StatusPill status={tone}>{label}</StatusPill>
            <div className="text-xs text-ink-subtle">
                {reasons.join(' · ')}
            </div>
        </div>
    );
}
